import React, { useState, useEffect } from 'react';
import { m, Variants } from 'framer-motion';

interface ScrollRevealProps {
	children: React.ReactNode;
	direction?: 'up' | 'down' | 'left' | 'right' | 'none';
	delay?: number;
	duration?: number;
	distance?: number;
	once?: boolean;
	style?: React.CSSProperties;
}

const ScrollReveal: React.FC<ScrollRevealProps> = ({
	children,
	direction = 'up',
	delay = 0,
	duration = 0.8,
	distance = 40,
	once = true,
	style,
}) => {
	const [isReady, setIsReady] = useState(false);

	useEffect(() => {
		// Only enable animations after the page has finished loading
		// This avoids layout thrashing during the critical path
		const handleLoad = () => setIsReady(true);

		if (document.readyState === 'complete') {
			setIsReady(true);
		} else {
			window.addEventListener('load', handleLoad);
		}
		return () => window.removeEventListener('load', handleLoad);
	}, []);

	const variants: Variants = {
		hidden: {
			opacity: 0,
			y: direction === 'up' ? distance : direction === 'down' ? -distance : 0,
			x: direction === 'left' ? distance : direction === 'right' ? -distance : 0,
		},
		visible: {
			opacity: 1,
			y: 0,
			x: 0,
			transition: {
				duration: duration,
				ease: [0.25, 0.1, 0.25, 1],
				delay: delay,
			},
		},
	};

	return (
		<m.div
			initial="hidden"
			whileInView={isReady ? 'visible' : 'hidden'}
			viewport={{ once, amount: 0.05 }}
			variants={variants}
			style={{
				...style,
				width: '100%',
			}}
		>
			{children}
		</m.div>
	);
};

export default ScrollReveal;
